/* What happened, in the two seconds staff can spare.
 *
 * A1 is the whole job: a name, a count, and the way out. A2 to A6 are the
 * states that only exist when something goes wrong, and "all of them decide
 * whether the tablet stays switched on".
 *
 * EVERY STRING COMES FROM THE COPY TABLE, KEYED BY THE REFUSAL CODE.
 * The services return `E_TOO_SOON` with a gap and a next time; this decides
 * what that reads like in the room. Staff never see the code. No refusal
 * implies the customer did anything wrong, which is a rule from the event spec
 * and the reason A2 blames the card's rule rather than the person.
 *
 * THE UNDO COUNTDOWN IS A REAL CLOCK
 * It counts the three minute staff window down from the moment of success. At
 * zero the button does not vanish, it changes: the window closing does not
 * make the mistake go away, it makes it a manager's job, and a control that
 * disappears leaves staff with no idea what to do next.
 */
import { Button, StampGrid } from "@damga/ds";
import { COPY, type CopyKey, t } from "@damga/kit";
import { useEffect, useState } from "react";

import type { EarnResponse } from "./Till";

export type Result =
  | { kind: "stamped"; body: EarnResponse }
  | { kind: "refused"; code: string; params: Record<string, unknown> }
  | { kind: "undone"; name: string }
  /** A reward handed over (2026-09-18): what it was, to say out loud. */
  | { kind: "given"; reward: string }
  | { kind: "notSaved" }
  | { kind: "held" }
  | { kind: "queueFull"; count: number }
  | { kind: "error" };

const STAFF_UNDO_SECONDS = 180;

export function Outcome({
  result,
  stage,
  count,
  remaining,
  onClear,
  onUndo,
  onGive,
}: {
  result: Result;
  /** From the kit. Drives the urgent line and nothing else on this screen. */
  stage?: string;
  count?: number;
  remaining?: string;
  onClear: () => void;
  onUndo: (entryId: string) => void;
  /** A stamp that completed the card can hand the reward over at once. */
  onGive?: (rewardEntryId: string) => void;
}) {
  // On a success the artboard puts "Next customer" and the undo on one row
  // beneath the member card, so the way forward and the way back sit together.
  // Everywhere else there is nothing to undo and the button stands alone.
  if (result.kind === "stamped" && !result.body.replayed) {
    return (
      <section className="outcome">
        <Stamped body={result.body} onUndo={onUndo} onClear={onClear} onGive={onGive} />
      </section>
    );
  }
  return (
    <section className="outcome">
      <Body result={result} stage={stage} count={count} remaining={remaining} onUndo={onUndo} />
      <Button size="counter" onClick={onClear} fullWidth>
        {t("counter.nextCustomer")}
      </Button>
    </section>
  );
}

function Body({
  result,
  stage,
  count,
  remaining,
  onUndo,
}: {
  result: Result;
  stage?: string;
  count?: number;
  remaining?: string;
  onUndo: (id: string) => void;
}) {
  if (result.kind === "stamped") return <Stamped body={result.body} onUndo={onUndo} />;
  if (result.kind === "given") {
    return (
      <Panel
        tone="plain"
        title={result.reward ? t("counter.given", "en", { reward: result.reward }) : t("counter.givenPlain")}
        body={t("counter.givenBody")}
      />
    );
  }
  if (result.kind === "undone") {
    return (
      <Panel tone="plain" title={t("undo.done", "en", { name: result.name })} body="" />
    );
  }
  if (result.kind === "queueFull") {
    // THE WALL. Its own state, not a louder version of "held", because nothing
    // was held: the customer offered a code and there is no stamp anywhere.
    //
    // The body says only what is true. It used to end "Take their number and
    // stamp it when this till is back online", which promised a recovery the
    // product cannot keep at this scale: override-earn is capped at five per
    // manager per twelve hours, shares that budget with reversals, and cannot
    // enrol a first-time customer at all. See copy.ts.
    return (
      <Panel
        tone="bad"
        title={t("queue.full.title")}
        body={t("queue.full.body")}
        merchant={t("queue.full.merchant", "en", { count: String(result.count) })}
      />
    );
  }
  if (result.kind === "held") {
    // THE STAMP IS ON THE DEVICE, AND THIS PANEL SAYS SO WITHOUT SAYING SAVED.
    //
    // The register is deliberately neither the calm grey of a success nor the
    // red of a refusal. Something real happened and it is not finished, and
    // both halves have to survive being read by the customer standing on the
    // other side of the till.
    //
    // "Don't show your code again" is the line that does the customer's work:
    // it tells them the stamp is not lost, that showing the code a second time
    // achieves nothing, and it does all that without the word "saved".
    return (
      <Panel
        tone={stage === "pressing" || stage === "last" ? "holding-urgent" : "holding"}
        title={t("queue.notSent")}
        body={`${t("queue.onDevice")} ${t("queue.dontShowAgain")}`}
        // RULING 03: merchant-first, and BELOW the customer's lines rather
        // than above. A placement convention and not a guarantee — the
        // customer is reading the same screen — which is acceptable because
        // the line is true and specific. The test is not "will they see it"
        // but "is it fine if they do".
        merchant={
          (stage === "pressing" || stage === "last") && count && remaining
            ? t("queue.urgent", "en", { count: String(count), remaining })
            : undefined
        }
      />
    );
  }
  if (result.kind === "notSaved") {
    // Still reachable: a redemption has no offline path, and a stamp taken
    // before this tablet has registered as a till cannot be synced later.
    // tone="bad", not "plain". It used to be a calm grey panel saying "Saved",
    // which is the visual register of a success as well as the words of one.
    return (
      <Panel tone="bad" title={t("offline.notSaved")} body={t("offline.notSavedBody")} />
    );
  }
  if (result.kind === "error") {
    return <Panel tone="bad" title={t("error.generic.title")} body={t("error.generic.body")} />;
  }
  return <Refused code={result.code} params={result.params} />;
}

function Stamped({
  body,
  onUndo,
  onClear,
  onGive,
}: {
  body: EarnResponse;
  onUndo: (id: string) => void;
  onClear?: () => void;
  onGive?: (rewardEntryId: string) => void;
}) {
  const [left, setLeft] = useState(STAFF_UNDO_SECONDS);
  const [asked, setAsked] = useState(false);

  useEffect(() => {
    const id = setInterval(() => setLeft((s) => Math.max(0, s - 1)), 1000);
    return () => clearInterval(id);
  }, []);

  // A3: the tablet's second tap returned the first result rather than writing
  // again. Staff need to be told the first one worked, or they will try a
  // third time.
  if (body.replayed) {
    return (
      <Panel
        tone="plain"
        title={t("duplicate.title")}
        body={t("duplicate.body", "en", {
          name: body.member_name ?? "This customer",
          balance: body.balance ?? 0,
          threshold: body.threshold ?? 0,
        })}
      />
    );
  }

  const balance = body.balance ?? 0;
  const threshold = body.threshold ?? 0;
  const remaining = Math.max(0, threshold - balance);
  const clock = `${Math.floor(left / 60)}:${String(left % 60).padStart(2, "0")}`;

  return (
    <div className="stamped">
      <h1 className="stamped-title">
        {body.tier_completed || remaining === 0
          ? t("counter.stampedComplete")
          : t("counter.stamped", "en", { remaining })}
      </h1>

      {/* The member card, matching A1: who, then the grid, then what it buys.
          The grid is here because the count is the second thing staff read out
          and a number alone does not show a customer how close they are. */}
      <div className="stamped-card">
        <div className="stamped-row">
          <p className="stamped-who">{body.member_name ?? body.member_masked ?? ""}</p>
          {body.member_masked && body.member_name && (
            <span className="mono muted">{body.member_masked}</span>
          )}
        </div>

        {/* FULL WHEN IT FILLED (2026-09-18). The balance after a completing stamp
            has the reward's stamps taken off, so "Reward earned" sat over an empty
            card in the end-to-end test. The card filled; draw it filled. */}
        {threshold > 0 && threshold <= 30 && (
          <StampGrid filled={body.tier_completed ? threshold : balance} total={threshold} size={26} />
        )}

        <p className="stamped-tail">
          {body.tier_name ? `${body.tier_name} at ${threshold}` : `${balance} of ${threshold}`}
        </p>
      </div>

      {/* WHAT HAPPENED TO THE ADDRESS, when one was given (047). Said beside the
          stamp rather than instead of it: the stamp landed either way, and the
          customer standing here was just told their card would be in the app. */}
      {body.email && (
        <p className={body.email.kept ? "stamped-email" : "stamped-email muted"} role="status">
          {emailLine(body.email)}
        </p>
      )}

      {/* THE REWARD, THERE AND THEN (2026-09-18). Until this "Reward earned" was
          the end of the road: nothing at the counter could hand one over. The
          same code the stamp was just taken with, so the customer reads
          nothing twice. */}
      {body.tier_completed && body.reward_entry_id && onGive && (
        <Button size="counter" variant="primary" onClick={() => onGive(body.reward_entry_id!)} fullWidth>
          {body.reward_description
            ? t("counter.giveNowWhat", "en", { reward: body.reward_description })
            : t("counter.giveNow")}
        </Button>
      )}

      <div className="stamped-foot">
        {onClear && (
          <Button size="counter" onClick={onClear}>
            {t("counter.nextCustomer")}
          </Button>
        )}
        {body.entry_id &&
          (asked ? (
            <span className="muted" role="status">
              {t("common.loading")}
            </span>
          ) : left > 0 ? (
            <button
              className="undo"
              onClick={() => {
                setAsked(true);
                onUndo(body.entry_id!);
              }}
            >
              {t("counter.undoWithTimer", "en", { clock })}
            </button>
          ) : (
            // Changed, not removed. The window closing makes this a manager's
            // job; it does not make the mistake disappear.
            <span className="muted stamped-expired">{t("E_MANAGER_REQUIRED.body")}</span>
          ))}
      </div>
    </div>
  );
}

/* The refusals. Keyed lookups with a fallback, so a code the client has never
 * heard of still produces a sentence rather than a blank panel. New refusal
 * codes get added server-side all the time; a client that renders nothing for
 * an unknown one fails silently at the till. */
function Refused({ code, params }: { code: string; params: Record<string, unknown> }) {
  const p = params as Record<string, string | number>;

  switch (code) {
    case "E_TOO_SOON":
      return (
        <Panel
          tone="plain"
          title={t("E_TOO_SOON.title")}
          body={t("E_TOO_SOON.body", "en", {
            gap: p.min_gap_minutes,
            next: formatTime(p.next_available_at),
          })}
          say={t("E_TOO_SOON.say", "en", { next: formatTime(p.next_available_at) })}
        />
      );
    case "E_OWN_ACCOUNT":
      return <Panel tone="plain" title={t("E_OWN_ACCOUNT.title")} body={t("E_OWN_ACCOUNT.body")} />;
    case "E_CODE_UNKNOWN":
      return <Panel tone="plain" title={t("E_CODE_UNKNOWN.title")} body={t("E_CODE_UNKNOWN.body")} />;
    // BOTH OF THESE FELL THROUGH TO "SOMETHING WENT WRONG" until 2026-09-17,
    // including E_CODE_AMBIGUOUS on the day it shipped: its words were added to
    // the copy table and never to this switch, and a check that found the words
    // in the bundle was taken for a check that the till says them.
    case "E_CODE_AMBIGUOUS":
      return <Panel tone="plain" title={t("E_CODE_AMBIGUOUS.title")} body={t("E_CODE_AMBIGUOUS.body")} />;
    case "E_CODE_SPENT":
      return <Panel tone="plain" title={t("E_CODE_SPENT.title")} body={t("E_CODE_SPENT.body")} />;
    case "E_CODE_EXPIRED":
      return <Panel tone="plain" title={t("E_CODE_EXPIRED.title")} body={t("E_CODE_EXPIRED.body")} />;
    case "E_MEMBER_CAP":
      return (
        <Panel
          tone="plain"
          title={t("E_MEMBER_CAP.title")}
          body={t("E_MEMBER_CAP.body", "en", { cap: p.cap })}
        />
      );
    case "E_STAFF_CAP":
      return <Panel tone="bad" title={t("E_STAFF_CAP.title")} body={t("E_STAFF_CAP.body")} />;
    case "E_PROGRAM_INACTIVE":
      return (
        <Panel tone="bad" title={t("E_PROGRAM_INACTIVE.title")} body={t("E_PROGRAM_INACTIVE.body")} />
      );
    // Two codes, two meanings, and until 2026-09-18 one sentence: "the three
    // minute window has passed" was E_NOT_REVERSIBLE's words, which means
    // "this entry is not a stamp". Each says its own now.
    case "E_MANAGER_REQUIRED":
      return (
        <Panel tone="plain" title={t("E_MANAGER_REQUIRED.title")} body={t("E_MANAGER_REQUIRED.body")} />
      );
    case "E_OFFLINE_LIMIT":
      return (
        <Panel
          tone="bad"
          title={t("E_OFFLINE_LIMIT.title")}
          body={t("E_OFFLINE_LIMIT.body", "en", { max_entries: p.max_entries })}
        />
      );
    default: {
      // THE FALLBACK THE COMMENT ABOVE PROMISED. A refusal with words in the copy
      // table says them, parameters and all, even if nobody added a case here.
      // Only a code with no words at all gets the generic panel. Still never the
      // code itself: staff never see one.
      const title = `${code}.title` as CopyKey;
      const body = `${code}.body` as CopyKey;
      if (title in COPY) {
        return <Panel tone="plain" title={t(title, "en", p)} body={body in COPY ? t(body, "en", p) : ""} />;
      }
      return <Panel tone="bad" title={t("error.generic.title")} body={t("error.generic.body")} />;
    }
  }
}

function Panel({
  tone,
  title,
  body,
  say,
  merchant,
}: {
  /** Merchant-only, rendered below the customer's lines. See ruling 03. */
  merchant?: string;
  // "holding" is neither. A stamp on the device is not a success and not a
  // refusal, and giving it either register would be the panel telling a
  // different story from the words in it — which is the exact failure the
  // grey "Saved" panel was.
  tone: "plain" | "bad" | "holding" | "holding-urgent";
  title: string;
  body: string;
  say?: string;
}) {
  return (
    <div className="till-panel" data-tone={tone}>
      <h1 className="till-panel-title">{title}</h1>
      {body && <p className="muted">{body}</p>}
      {say && (
        <p className="say">
          <span className="label">Say this</span>
          {say}
        </p>
      )}
      {/* BELOW the customer's lines, always. Ruling 03: the placement is the
          convention, and the sentence is written so that a customer reading it
          learns something true rather than overhearing something meant for
          someone else. */}
      {merchant && <p className="merchant-note">{merchant}</p>}
    </div>
  );
}

function formatTime(value: unknown): string {
  if (typeof value !== "string") return "";
  const at = new Date(value);
  if (Number.isNaN(at.getTime())) return "";
  // The merchant's own clock. The server sends an instant; the till is
  // standing in the shop, so the shop's local rendering is the right one.
  return at.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });
}

/** One sentence for what happened to the address. Four happen in practice; the
 *  rest can only come from a client that skipped its own checks. */
function emailLine(email: { kept: boolean; reason: string | null }): string {
  if (email.kept) return t("enrolEmail.kept");
  switch (email.reason) {
    case "existing_card":
      return t("enrolEmail.existing_card");
    case "has_account":
      return t("enrolEmail.has_account");
    case "not_available":
      return t("enrolEmail.not_available");
    default:
      return t("enrolEmail.other");
  }
}
